class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";
  }

  connectedCallback() {
    window.FoxThemeEvents.subscribe(`ON_ITEM_ADDED`, async (state) => {
      this.renderContents(state);
    });
  }

  renderContents(parsedState, renderFooter = true) {
    // toggle(name, force) : force=false retire la classe (comportement
    // identique à l'ancien classList.remove quand parsedState.item_count est
    // absent, ex: payload de ON_ITEM_ADDED qui n'a pas ce champ). Mais quand
    // item_count est bien fourni (ex: refresh après suppression du dernier
    // article via CartGuarantee), on doit aussi pouvoir RE-METTRE la classe
    // is-empty : l'ancien code ne faisait que la retirer, jamais la remettre,
    // donc l'état "panier vide" ne s'affichait plus une fois qu'un ajout
    // avait eu lieu dans la session.
    this.classList.toggle("is-empty", parsedState.item_count === 0);
    this.getSectionsToRender(renderFooter).forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    setTimeout(() => {
      if (this.openWhenAdded && !this.drawer.isOpen()) {
        this.drawer.openDrawer();
        document.body.classList.add("prevent-scroll");
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
        window._app.init();
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
    ];

    if (renderFooter) {
      sections.push({
        id: "cart-drawer",
        selector: ".f-drawer__footer",
      });
    }

    return sections;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") },
    );
  }
  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__block-subtotal",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__buttons",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class CartGuarantee extends HTMLElement {
  constructor() {
    super();
    this.casseVariantId = this.dataset.casseId ? parseInt(this.dataset.casseId) : null;
    this.volVariantId = this.dataset.volId ? parseInt(this.dataset.volId) : null;
    this.inputs = this.querySelectorAll("[data-guarantee-input]");
    this.modeButtons = this.querySelectorAll("[data-guarantee-mode]");
    // Abonnement mensuel ("sub", selling plan) ou 1 mois sans abonnement ("once") — rendu initial
    // en Liquid (snippets/cart-drawer.liquid), recalé ensuite sur la garantie du panier.
    this.mode = this.dataset.mode || "once";
    this.zeroLabel = this.dataset.zeroLabel || "0 €";
    // Garantie déjà au panier : résumé compact + "Modifier" (classe is-collapsed). expanded = le
    // client a cliqué "Modifier" — on laisse alors le sélecteur complet ouvert.
    this.editButton = this.querySelector("[data-guarantee-edit]");
    this.expanded = false;
    this._refreshing = false;
    this._processing = false;
  }

  connectedCallback() {
    this.inputs.forEach((input) => {
      input.addEventListener("change", this.onChange.bind(this));
    });
    this.modeButtons.forEach((button) => {
      button.addEventListener("click", () => this.onModeChange(button.dataset.guaranteeMode));
    });
    if (this.editButton) {
      this.editButton.addEventListener("click", () => {
        this.expanded = true;
        this.classList.remove("is-collapsed");
      });
    }

    this.syncFromCart();

    window.FoxThemeEvents.subscribe("ON_CART_UPDATED", () => {
      if (!this._refreshing) {
        this.syncFromCart();
      }
    });
    // Ce bloc est hors des zones re-rendues par CartDrawer.renderContents() : la pop-up garantie
    // ajoute plaque + garantie en UNE requête, qui n'émet que ON_ITEM_ADDED (pas ON_CART_UPDATED)
    // — sans cet abonnement, le bloc restait sur l'état "panier vide" jusqu'au rechargement.
    window.FoxThemeEvents.subscribe("ON_ITEM_ADDED", () => {
      if (!this._refreshing) {
        this.syncFromCart();
      }
    });
  }

  async syncFromCart() {
    let cart = await FoxThemeCartHelpers.getState();
    if (!cart) return;

    if (!this._processing) {
      const fixed = await this.enforceExclusivity(cart);
      if (fixed) {
        this._processing = true;
        this._refreshing = true;
        await this.refreshCart();
        this._processing = false;
        this._refreshing = false;
        cart = await FoxThemeCartHelpers.getState();
        if (!cart) return;
      }
    }

    const cartVariantIds = cart.items.map((i) => i.variant_id);

    if (this.volVariantId && cartVariantIds.includes(this.volVariantId)) {
      this.setSelected("vol");
    } else if (this.casseVariantId && cartVariantIds.includes(this.casseVariantId)) {
      this.setSelected("casse");
    } else {
      this.setSelected("none");
    }

    // Mode calé sur la garantie du panier (ex. ajoutée en abonnement depuis la pop-up produit).
    const current = this.findGuarantee(cart);
    if (current && this.modeButtons.length) {
      this.mode = current.selling_plan_allocation ? "sub" : "once";
    }
    this.render();
    this.updateCompact(current);

    // La garantie n'a de sens que rattachée à un produit réel : on la cache
    // dès qu'il ne reste plus qu'elle seule dans le panier (pas seulement
    // quand le panier est totalement vide). enforceExclusivity() retire déjà
    // la garantie dans ce cas, mais ce toggle couvre aussi l'instant avant
    // que ce fetch/refresh n'ait fini de tourner.
    this.classList.toggle("is-empty", !this.hasRealItems(cart));
  }

  hasRealItems(cart) {
    const guaranteeIds = [this.casseVariantId, this.volVariantId].filter(Boolean);
    return cart.items.some((i) => !guaranteeIds.includes(i.variant_id));
  }

  // Garde-fou: le panier ne doit jamais contenir les deux garanties en même
  // temps, ni plus d'une unité de la même garantie (ex: ajoutées depuis deux
  // fiches produit différentes sans passer par ce composant). On garde la
  // garantie "casse + vol" (superset) si les deux sont présentes, et on
  // ramène toute quantité > 1 à 1. Retourne true si le panier a été corrigé.
  async enforceExclusivity(cart) {
    const casseItem = this.casseVariantId
      ? cart.items.find((i) => i.variant_id === this.casseVariantId)
      : null;
    const volItem = this.volVariantId
      ? cart.items.find((i) => i.variant_id === this.volVariantId)
      : null;

    let changed = false;

    if (casseItem && volItem) {
      await this.changeQuantity(casseItem.key, 0);
      changed = true;
    }

    const remaining = casseItem && volItem ? volItem : volItem || casseItem;

    if (remaining) {
      // Une garantie n'a de sens que rattachée à un produit réel. Si le
      // dernier produit "normal" est retiré du panier (ex: suppression de
      // la ligne principale) alors qu'une garantie y est toujours présente,
      // on la retire aussi : sinon elle reste invisible (ligne masquée en
      // CSS) et facturée sans qu'on puisse la voir ni la retirer.
      if (!this.hasRealItems(cart)) {
        await this.changeQuantity(remaining.key, 0);
        return true;
      }

      // Garantie liée à une plaque (property cachée _linked_to, pop-up garantie) dont la plaque
      // n'est plus dans le panier : retirée aussi (filet de sécurité, cf. assets/cart.js).
      const linkedId = remaining.properties && remaining.properties._linked_to;
      if (linkedId && !cart.items.some((i) => i.key !== remaining.key && i.properties && i.properties._linked_to === linkedId)) {
        await this.changeQuantity(remaining.key, 0);
        return true;
      }

      if (remaining.quantity > 1) {
        await this.changeQuantity(remaining.key, 1);
        changed = true;
      }
    }

    return changed;
  }

  async changeQuantity(key, quantity) {
    await fetch(FoxThemeSettings.routes.cart_change_url, {
      ...fetchConfig,
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  findGuarantee(cart) {
    const guaranteeIds = [this.casseVariantId, this.volVariantId].filter(Boolean);
    return cart.items.find((i) => guaranteeIds.includes(i.variant_id)) || null;
  }

  // Prix, notes et conditions selon le mode — mêmes libellés que la pop-up produit
  // (src/js/modules/pdp-guarantee.js). Une garantie sans plan n'est pas proposée en abonnement.
  render() {
    const sub = this.mode === "sub";
    this.modeButtons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.guaranteeMode === this.mode));
    });
    this.querySelectorAll('[data-guarantee-option]:not([data-guarantee-option="none"])').forEach((option) => {
      const input = option.querySelector("[data-guarantee-input]");
      const price = option.querySelector("[data-guarantee-price]");
      const note = option.querySelector("[data-guarantee-note]");
      const unavailable = sub && !option.dataset.planId;
      if (input) input.disabled = unavailable;
      option.classList.toggle("is-disabled", unavailable);
      // Même présentation que la pop-up : prix mensuel à droite, "2e mois offert" en dessous.
      const free = sub && !unavailable;
      if (price) {
        price.textContent = unavailable ? "—" : free ? `${option.dataset.subPrice}/mois` : option.dataset.oncePrice;
        price.classList.toggle("f-guarantee-popup__price--free", free);
      }
      if (note) {
        note.textContent = unavailable
          ? "Indisponible en abonnement"
          : free
            ? "2e mois offert"
            : "Couvert 1 mois";
        note.classList.toggle("f-guarantee-popup__note--muted", free);
      }
    });
    const terms = this.querySelector("[data-guarantee-terms]");
    if (terms) {
      terms.textContent = sub
        ? "Facturation mensuelle, sans engagement. Résiliable à tout moment depuis votre compte, en 2 clics."
        : "Paiement unique, couverture d’un mois, aucune reconduction.";
    }
  }

  // Changement de mode : si une garantie est cochée, elle est remplacée par la même dans le
  // nouveau mode (avec ou sans selling plan).
  onModeChange(mode) {
    if (this._processing || mode === this.mode) return;
    this.mode = mode;
    this.render();
    const checked = this.querySelector("[data-guarantee-input]:checked");
    const option = checked && checked.closest("[data-guarantee-option]");
    if (!option || option.dataset.guaranteeOption === "none") return;
    if (mode === "sub" && !option.dataset.planId) {
      // Garantie sans abonnement possible : on la retire plutôt que de la garder en ponctuel.
      this.setSelected("none");
      this.applySelection("none");
      return;
    }
    this.applySelection(option.dataset.guaranteeOption);
  }

  setSelected(value) {
    this.inputs.forEach((input) => {
      const option = input.closest("[data-guarantee-option]");
      if (option && option.dataset.guaranteeOption === value) {
        input.checked = true;
      }
    });
  }

  onChange(event) {
    const option = event.target.closest("[data-guarantee-option]").dataset.guaranteeOption;
    this.applySelection(option);
  }

  // Remplace la garantie du panier par `option` (casse / vol / none) dans le mode courant. Les
  // properties cachées de la garantie actuelle (_linked_to, _immatriculation : lien avec sa plaque,
  // cf. pop-up produit) sont conservées sur la nouvelle.
  async applySelection(option) {
    if (this._processing) return;
    this._processing = true;
    this._refreshing = true;

    const cart = await FoxThemeCartHelpers.getState();
    if (!cart) {
      this._processing = false;
      this._refreshing = false;
      return;
    }

    const current = this.findGuarantee(cart);
    const properties = {};
    if (current && current.properties) {
      ["_linked_to", "_immatriculation"].forEach((key) => {
        if (current.properties[key]) properties[key] = current.properties[key];
      });
    }

    await this.removeGuarantees(cart);

    if (option !== "none") {
      const variantId = option === "casse" ? this.casseVariantId : this.volVariantId;
      const optionEl = this.querySelector(`[data-guarantee-option="${option}"]`);
      const planId = optionEl && optionEl.dataset.planId ? parseInt(optionEl.dataset.planId, 10) : null;
      const sellingPlanId = this.mode === "sub" && this.modeButtons.length ? planId : null;
      if (variantId) await this.addGuarantee(variantId, { sellingPlanId, properties });
    }

    await this.refreshCart();
    this._processing = false;
    this._refreshing = false;

    // Choix appliqué : on revient au résumé compact s'il reste une garantie (rien à replier sinon).
    this.expanded = false;
    const after = await FoxThemeCartHelpers.getState();
    if (after) this.updateCompact(this.findGuarantee(after));
  }

  // Résumé compact : replié dès qu'une garantie est au panier (sauf après "Modifier"), texte
  // reconstruit depuis la ligne du panier et les data-attributs de l'option correspondante.
  updateCompact(current) {
    if (!this.editButton) return;
    const collapsed = !!current && !this.expanded;
    this.classList.toggle("is-collapsed", collapsed);
    if (!current) return;
    const optionName = current.variant_id === this.volVariantId ? "vol" : "casse";
    const option = this.querySelector(`[data-guarantee-option="${optionName}"]`);
    if (!option) return;
    const title = this.querySelector("[data-guarantee-compact-title]");
    const detail = this.querySelector("[data-guarantee-compact-detail]");
    if (title) title.textContent = option.dataset.title || current.product_title;
    if (detail) {
      detail.textContent = current.selling_plan_allocation
        ? `Abonnement · ${option.dataset.subPrice}/mois · 2e mois offert`
        : `1 mois sans abonnement · ${option.dataset.oncePrice}`;
    }
  }

  async removeGuarantees(cart) {
    const guaranteeIds = [this.casseVariantId, this.volVariantId].filter(Boolean);

    for (const item of cart.items) {
      if (guaranteeIds.includes(item.variant_id)) {
        // Use item.key (stable unique identifier) instead of line index which shifts after each removal
        await fetch(FoxThemeSettings.routes.cart_change_url, {
          ...fetchConfig,
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    }
  }

  async addGuarantee(variantId, { sellingPlanId = null, properties = {} } = {}) {
    const item = { id: variantId, quantity: 1 };
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    if (Object.keys(properties).length) item.properties = properties;
    const response = await fetch(FoxThemeSettings.routes.cart_add_url, {
      ...fetchConfig,
      body: JSON.stringify(item),
    });
    return response.json();
  }

  async refreshCart() {
    // Use /cart/update.js with sections to get re-rendered HTML without changing cart contents
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        sections: ["cart-drawer"],
        sections_url: window.location.pathname,
      }),
    });
    const parsedState = await response.json();

    if (window.FoxTheme.Cart && parsedState.sections) {
      window.FoxTheme.Cart.renderContents(parsedState);
    }

    // Emit so other components (cart goal, etc.) stay in sync
    window.FoxThemeEvents.emit("ON_CART_UPDATED", parsedState);

    // Override the count set by ON_CART_UPDATED subscriber : nombre de lignes parentes/autonomes
    // (jamais les enfants imbriqués via parent_id, cf. assets/nested-line-items.js), en excluant
    // aussi les lignes garantie — comportement déjà existant, adapté au comptage par ligne plutôt
    // que par quantité.
    const displayCount = this.countDisplayableLines(parsedState);
    FoxThemeCartHelpers.updateCartCount(displayCount);
  }

  countDisplayableLines(cart) {
    const guaranteeIds = [this.casseVariantId, this.volVariantId].filter(Boolean);
    return cart.items.filter((item) => {
      const isChild = window.FoxThemeNestedCart.getParentKey(item) !== undefined;
      const isGuarantee = guaranteeIds.includes(item.variant_id);
      return !isChild && !isGuarantee;
    }).length;
  }
}

customElements.define("cart-guarantee", CartGuarantee);

// Dernier filet de sécurité avant checkout: si pour une raison quelconque le
// panier contient les deux garanties (ou plus d'une unité d'une même
// garantie), on corrige avant de laisser passer la navigation vers /checkout.
document.addEventListener(
  "click",
  async (event) => {
    const button = event.target.closest("[data-checkout-button], #checkout");
    if (!button || button.dataset.guaranteeGuardPassed === "true") return;

    const guaranteeEl = document.querySelector("cart-guarantee");
    if (!guaranteeEl) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const cart = await FoxThemeCartHelpers.getState();
    if (cart) {
      const fixed = await guaranteeEl.enforceExclusivity(cart);
      if (fixed) await guaranteeEl.refreshCart();
    }

    button.dataset.guaranteeGuardPassed = "true";
    const form = button.form || button.closest("form");
    if (form && form.requestSubmit) {
      form.requestSubmit(button);
    } else if (form) {
      form.submit();
    } else {
      button.click();
    }
  },
  true,
);